import { HubFeature } from '@ats/api/cm';
import { LocDefs, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Candidate|Program History|Continuing Education|Suspend Grantable Certs';

const instructions = `
<p>
    Your certification is expired. You have up to 30 days following your expiration date to pay fees and/or submit any CEUs that were earned within your three-year renewal cycle.
</p>
<p>ATTENTION - Please DO NOT click on the "Activate Submission Grace Period" button if your certification has expired and you plan on taking a CompTIA exam. Taking an exam during your Grace Period does not renew your current certifications and will cause any new certification not to display in your record.</p>
<p>NOTE: This 30-day submission grace period is NOT an extension to earn CEUs. All CEUs must be earned within your three-year renewal cycle, and any CEUs earned after your expiration date are NOT VALID toward renewal. CEUs earned after your expiration date may be invalidated and your certification revoked.</p>
<p>You cannot pay fees or upload CEUs beyond 30 days after your expiration date. No exceptions will be granted.</p>
`;

export type SPSuspendGrantableCertsLocs =
	| 'SPSuspendGrantableCertsSectionTitle'
	| 'SPSuspendGrantableCertsTableCertCatalogName'
	| 'SPSuspendGrantableCertsTableActiveDate'
	| 'SPSuspendGrantableCertsTableInactiveDate'
	| 'SPSuspendGrantableCertsInstructions'
	| 'SPSuspendGrantableCertsActivateGracePeriodButton'
	| 'SPSuspendGrantableCertsSuspendFailedNotification'
	| 'SPSuspendGrantableCertsSuspendSuccessNotification';

export const spSuspendGrantableCertsLocs: LocDefs<SPSuspendGrantableCertsLocs> = {
	SPSuspendGrantableCertsSectionTitle: {
		defaultValue: 'Expired Certification(s)',
		description: 'Title displayed in the expired certification(s) section.',
		displayPath: `${dpRoot}|Section Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsTableCertCatalogName: {
		defaultValue: 'Name',
		description: 'Table column header for the certification name.',
		displayPath: `${dpRoot}|Certification`,
		displayType: 'Label',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsTableActiveDate: {
		defaultValue: 'Start Date',
		description: 'Table column header for the active date.',
		displayPath: `${dpRoot}|Active Date`,
		displayType: 'Label',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsTableInactiveDate: {
		defaultValue: 'Expiration',
		description: 'Table column header for the inactive date.',
		displayPath: `${dpRoot}|Inactive Date`,
		displayType: 'Label',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsInstructions: {
		defaultValue: instructions,
		description: 'Instructions displayed in the expired certification(s) section.',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsActivateGracePeriodButton: {
		defaultValue: 'Activate Submission Grace Period',
		description: 'Button to activate the submission grace period.',
		displayPath: `${dpRoot}|Activate Submission Grace Period`,
		displayType: 'Button',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsSuspendFailedNotification: {
		defaultValue: 'Requests for the following certifications failed:',
		description: 'Notification displayed when the suspend operation fails.',
		displayPath: `${dpRoot}|Suspend Failed Notification`,
		displayType: 'Notification',
		feature: HubFeature.CredentialComptiaSelfExtend,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPSuspendGrantableCertsSuspendSuccessNotification: {
		defaultValue: 'Submission Grace Period requested successfully',
		description:
			'Notification displayed when the submission grace period is requested successfully.',
		displayPath: `${dpRoot}|Suspend Success Notification`,
		displayType: 'Notification',
		feature: HubFeature.CredentialComptiaSelfExtend,
		portal: 'SP',
		htmlAllowed: false,
		shared: false,
	},
};

export const spSuspendGrantableCertsLocKeys = MapLocKeys<SPSuspendGrantableCertsLocs>(
	spSuspendGrantableCertsLocs
);

export type SPSuspendGrantableCertsLocKeys = typeof spSuspendGrantableCertsLocKeys;
